import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Repeat, Pause, Play, Trash2, Zap, Calendar, ShoppingBag } from "lucide-react";
import { toast, Toaster } from "sonner";

const CADENCES = [
    { v: "weekly", label: "Every week" },
    { v: "fortnightly", label: "Every 2 weeks" },
    { v: "monthly", label: "Every month" },
    { v: "every_60_days", label: "Every 60 days" },
    { v: "every_90_days", label: "Every 90 days" },
];

export default function MySubscriptions() {
    const [rows, setRows] = useState([]);
    const load = async () => {
        const { data } = await api.get("/subscriptions/mine");
        setRows(data);
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (s, status) => {
        try {
            await api.put(`/subscriptions/${s.id}`, { status });
            toast.success(status === "active" ? "Resumed" : status === "paused" ? "Paused" : "Cancelled");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail); }
    };
    const setCadence = async (s, cadence) => {
        try { await api.put(`/subscriptions/${s.id}`, { cadence }); toast.success("Frequency updated"); await load(); }
        catch (e) { toast.error(e.response?.data?.detail); }
    };
    const runNow = async (s) => {
        if (!window.confirm(`Place a refill order now for ${s.product_name}?`)) return;
        try {
            const { data } = await api.post(`/subscriptions/${s.id}/run-now`);
            toast.success(`Order placed · next refill on ${data.next_run}`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't place refill order"); }
    };
    const cancel = async (s) => {
        if (!window.confirm("Cancel this subscription? You can re-subscribe anytime.")) return;
        try { await api.delete(`/subscriptions/${s.id}`); toast.success("Cancelled"); await load(); } catch (e) { toast.error(e.response?.data?.detail); }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 py-12" data-testid="my-subscriptions-page">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-sky-700">My account</div>
            <h1 className="font-serif text-4xl text-stone-900 mt-2 inline-flex items-center gap-3"><Repeat className="w-7 h-7 text-sky-700" />Auto-refill subscriptions</h1>
            <p className="text-sm text-stone-500 mt-2">Never run out of your remedies — we'll ship them on your schedule and you can change or cancel anytime.</p>

            {rows.length === 0 ? (
                <div className="bg-stone-50 border border-stone-200 p-12 text-center mt-8" data-testid="subs-empty">
                    <Repeat className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                    <p className="text-sm text-stone-600">You don't have any auto-refills yet. Add one from any product page.</p>
                    <Link to="/shop" className="text-sky-700 hover:underline text-sm inline-flex items-center gap-1 mt-3"><ShoppingBag className="w-3 h-3" />Browse remedies</Link>
                </div>
            ) : (
                <div className="space-y-3 mt-8">
                    {rows.map(s => (
                        <div key={s.id} className="bg-white border border-stone-200 p-4" data-testid={`sub-row-${s.id}`}>
                            <div className="grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-12 sm:col-span-5">
                                    <div className="flex gap-3">
                                        {s.product_image && <img src={s.product_image} alt={s.product_name} className="w-16 h-16 bg-stone-100 object-cover" />}
                                        <div>
                                            <div className="font-medium text-stone-900"><Link to={`/product/${s.product_slug}`} className="hover:text-sky-700">{s.product_name}</Link></div>
                                            <div className="text-xs text-stone-500">{s.size_label} · qty {s.quantity}</div>
                                            <div className="text-xs text-stone-500">{formatINR(s.unit_price * s.quantity)} / refill</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-span-6 sm:col-span-2">
                                    <Select value={s.cadence} onValueChange={(v) => setCadence(s, v)} disabled={s.status === "cancelled"}>
                                        <SelectTrigger className="rounded-none border-stone-300 h-9 text-xs" data-testid={`sub-cadence-${s.id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>{CADENCES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-6 sm:col-span-2 text-xs">
                                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Next refill</div>
                                    <div className={`font-medium ${s.next_run_due && s.status === "active" ? "text-amber-700" : "text-stone-900"}`}><Calendar className="inline w-3 h-3 mr-1" />{s.next_run || "—"}</div>
                                </div>
                                <div className="col-span-12 sm:col-span-3 flex flex-wrap gap-1 justify-end">
                                    {s.status === "active" && (
                                        <>
                                            <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-sky-300 text-sky-700" onClick={() => runNow(s)} data-testid={`sub-run-${s.id}`}><Zap className="w-3 h-3 mr-1" />Refill now</Button>
                                            <Button size="sm" variant="outline" className="rounded-none h-7 px-2" onClick={() => setStatus(s, "paused")} data-testid={`sub-pause-${s.id}`}><Pause className="w-3 h-3 mr-1" />Pause</Button>
                                        </>
                                    )}
                                    {s.status === "paused" && (
                                        <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-emerald-300 text-emerald-700" onClick={() => setStatus(s, "active")} data-testid={`sub-resume-${s.id}`}><Play className="w-3 h-3 mr-1" />Resume</Button>
                                    )}
                                    {s.status !== "cancelled" && (
                                        <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-red-300 text-red-700" onClick={() => cancel(s)} data-testid={`sub-cancel-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
                                    )}
                                </div>
                                <div className="col-span-12 text-[11px] text-stone-500 flex items-center gap-3 flex-wrap">
                                    <span><strong>{s.orders_count || 0}</strong> orders shipped</span>
                                    <span>·</span>
                                    <span>Ships to {s.address?.city}, {s.address?.state}</span>
                                    <span>·</span>
                                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${s.status === "active" ? "bg-emerald-100 text-emerald-800" : s.status === "paused" ? "bg-amber-100 text-amber-800" : "bg-stone-200 text-stone-600"}`}>{s.status}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
